import { useEffect, useState, useCallback } from "react";
import api, { API_URL } from "@/lib/api";
import { toast } from "sonner";
import { fmtINRFull as fmtINR, fmtNum } from "@/lib/format";
import { Download, CheckCircle2, Loader2 } from "lucide-react";
import { RecoGapTrend } from "./_reco_gap_trend";
import { RecoUnknownStores } from "./_reco_unknown_stores";

const TABS = [
  { kind: "missing", label: "Missing in portal", note: "will be added" },
  { kind: "mismatch", label: "Different", note: "will be corrected to match the file" },
  { kind: "extra", label: "In portal, not in file", note: "flagged only — not changed" },
];

const Stat = ({ label, value, testid, tone = "" }) => (
  <div className="border border-black/10 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className={`font-display text-xl ${tone}`}>{value}</div>
  </div>
);

const download = (id, kind) => {
  fetch(`${API_URL}/reco/invoice/analyses/${id}/export?kind=${kind}`, { headers: { Authorization: `Bearer ${localStorage.getItem("kazo_token")}` } })
    .then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
    .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `reco_${kind}.csv`; a.click(); URL.revokeObjectURL(u); })
    .catch(() => toast.error("Export failed"));
};

const Rows = ({ rows }) => (
  <table className="w-full text-xs">
    <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b">
      <th className="py-1.5 pr-3">Bill</th><th className="pr-3">Store</th><th className="pr-3">Date</th>
      <th className="pr-3 text-right">File ₹</th><th className="pr-3 text-right">Portal ₹</th><th>What differs</th>
    </tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={`${r.kind}-${r.bill_number}`} className="border-b border-black/5">
        <td className="py-1 pr-3 font-mono">{r.bill_number}</td><td className="pr-3">{r.store_code}</td><td className="pr-3">{r.day}</td>
        <td className="pr-3 text-right">{r.file ? fmtINR(r.file.total) : "—"}</td>
        <td className="pr-3 text-right">{r.portal ? fmtINR(r.portal.paid) : "—"}</td>
        <td>{(r.diffs || []).join(", ") || "—"}</td>
      </tr>))}
    </tbody>
  </table>
);

export const RecoAnalysis = ({ id, onChanged }) => {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("missing");
  const [applying, setApplying] = useState(false);
  const [sendSms, setSendSms] = useState(true);

  const load = useCallback(() => api.get(`/reco/invoice/analyses/${id}`, { params: { kind, limit: 100 } }).then((r) => setData(r.data)).catch(() => {}), [id, kind]);
  useEffect(() => { load(); }, [load]);
  const a = data?.analysis;
  useEffect(() => {
    if (!a || !["analysing", "applying"].includes(a.status)) return undefined;
    const t = setInterval(() => { load(); onChanged(); }, 4000);
    return () => clearInterval(t);
  }, [a, load, onChanged]);

  if (!a) return <div className="text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (a.status === "analysing") return <div className="chart-card p-5 text-sm flex items-center gap-2" data-testid="reco-analysing"><Loader2 className="w-4 h-4 animate-spin" /> Comparing {a.filename} with the portal…</div>;
  if (a.status === "failed") return <div className="chart-card p-5 text-sm text-rose-700" data-testid="reco-failed">Could not read the file: {a.error}</div>;

  const c = a.counts || {}, t = a.totals || {};
  const apply = async () => {
    if (!window.confirm(`Add ${fmtNum(c.missing)} missing bills (with points${sendSms ? " and SMS" : ", no SMS"}) and correct ${fmtNum(c.mismatch)} different bills?`)) return;
    setApplying(true);
    try { await api.post(`/reco/invoice/analyses/${id}/apply`, { send_sms: sendSms }); toast.success("Applying — dashboards update when done"); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  return (
    <div className="chart-card p-5 space-y-4" data-accent="teal" data-testid="reco-analysis">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-lg">{a.filename} · {a.period_start} to {a.period_end} · {a.stores} {a.stores === 1 ? "store" : "stores"}</h3>
        {a.status === "applied" && <span className="text-emerald-700 text-sm flex items-center gap-1" data-testid="reco-applied"><CheckCircle2 className="w-4 h-4" /> Applied — {fmtNum(a.apply_result?.added)} added, {fmtNum(a.apply_result?.corrected)} corrected, {fmtNum(a.apply_result?.points_credited)} pts credited, {fmtNum(a.apply_result?.points_reversed)} pts reversed</span>}
        {a.status === "applying" && <span className="text-amber-700 text-sm flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Applying…</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="Bills in file" value={fmtNum(c.file_bills)} testid="reco-stat-file" />
        <Stat label="Match" value={fmtNum(c.same)} testid="reco-stat-same" tone="text-emerald-700" />
        <Stat label="Missing" value={fmtNum(c.missing)} testid="reco-stat-missing" tone="text-rose-700" />
        <Stat label="Different" value={fmtNum(c.mismatch)} testid="reco-stat-mismatch" tone="text-amber-700" />
        <Stat label="Extra in portal" value={fmtNum(c.extra)} testid="reco-stat-extra" />
        <Stat label="Unknown store" value={fmtNum(c.unknown_store_bills)} testid="reco-stat-unknown" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
        <Stat label="File sales (G_Amt)" value={fmtINR(t.file_total)} testid="reco-total-file" />
        <Stat label={a.status === "applied" ? "Portal sales before apply" : "Portal sales now (same days/stores)"} value={fmtINR(t.portal_total_before)} testid="reco-total-portal" />
        <Stat label={a.status === "applied" ? "Gap before apply" : "Gap"} value={fmtINR((t.file_total || 0) - (t.portal_total_before || 0))} testid="reco-total-gap" />
      </div>
      <RecoUnknownStores analysis={a} onDone={() => { load(); onChanged(); }} />
      <RecoGapTrend gap={a.gap} applied={a.status === "applied"} />
      <div className="flex flex-wrap gap-2 border-b border-black/10">
        {TABS.map((tb) => (
          <button key={tb.kind} onClick={() => setKind(tb.kind)} data-testid={`reco-tab-${tb.kind}`}
            className={`px-3 py-2 text-sm ${kind === tb.kind ? "border-b-2 border-black font-medium" : "text-neutral-500"}`}>{tb.label}</button>
        ))}
        <button onClick={() => download(id, kind)} className="ml-auto k-btn k-btn-ghost k-btn-sm" data-testid="reco-export"><Download className="w-3.5 h-3.5" /> Export</button>
      </div>
      <div className="text-xs text-neutral-500">{TABS.find((x) => x.kind === kind).note} · showing first 100</div>
      <div className="max-h-80 overflow-auto"><Rows rows={data.rows} /></div>
      {a.apply_blocked && <div className="text-sm text-rose-700 border border-rose-200 bg-rose-50 p-3" data-testid="reco-apply-blocked">{a.apply_blocked}</div>}
      {a.status === "analysed" && !a.apply_blocked && (
        <div className="flex flex-wrap items-center gap-4">
        <label className="text-sm flex items-center gap-2" data-testid="reco-send-sms">
          <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} data-testid="reco-send-sms-checkbox" />
          Send the usual purchase / points SMS to customers
        </label>
        <button onClick={apply} disabled={applying || (!c.missing && !c.mismatch)} className="k-btn kazo-bg-burgundy text-white" data-testid="reco-apply-btn">
          {applying ? "Starting…" : `Apply — add ${fmtNum(c.missing)} & correct ${fmtNum(c.mismatch)}`}
        </button>
        </div>
      )}
    </div>
  );
};
